import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AuthError } from "aws-amplify/auth";
import type React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { cognitoErrorNames, useAuth } from "../hooks/useAuth";
import { type ResetPWFormValue, resetPWSchema } from "../schemas";

export const ResetPW = (): React.ReactElement => {
	const { handleConfirmResetPassword } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const email = searchParams.get("email") || "";

	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		clearErrors,
	} = useForm<ResetPWFormValue>({
		resolver: zodResolver(resetPWSchema),
	});

	const onSubmit = async (data: ResetPWFormValue) => {
		clearErrors("root");
		if (!email) return;
		setIsLoading(true);
		try {
			await handleConfirmResetPassword({
				username: email,
				...data,
			});
			navigate("/login");
		} catch (err) {
			if (err instanceof AuthError) {
				switch (err.name) {
					case cognitoErrorNames.ConfirmResetPassword.UserNotFound:
						alert("メールアドレスが正しくありません。");
						break;
					case cognitoErrorNames.ConfirmResetPassword.CodeMismatch:
						setError("confirmationCode", {
							message: "認証コードが正しくありません。",
						});
						break;
				}
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				border: "1px solid #d0d7de",
				borderRadius: 1,
				padding: 2,
				backgroundColor: "#f5f5f5",
			}}
		>
			<Typography component="h2" variant="h5" sx={{ mb: 2 }}>
				パスワードの再設定
			</Typography>
			<Box
				component="form"
				onSubmit={handleSubmit(onSubmit)}
				noValidate
				sx={{
					width: "100%",
					mt: 1,
				}}
			>
				<Typography
					variant="h6"
					sx={{
						textAlign: "center",
						wordBreak: "break-word",
					}}
				>
					{email}
				</Typography>
				<TextField
					margin="normal"
					required
					fullWidth
					label={"認証コード"}
					autoFocus
					autoComplete="one-time-code"
					sx={{
						backgroundColor: "white",
					}}
					{...register("confirmationCode")}
					error={Boolean(errors.confirmationCode)}
				/>
				<Typography variant="caption" color="error" sx={{ display: "block" }}>
					{errors.confirmationCode?.message}
				</Typography>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						mt: 2,
					}}
				>
					パスワードは「数字」「特殊文字」「大文字」「小文字」を含む8文字以上で設定してください。
				</Typography>
				<TextField
					margin="normal"
					required
					fullWidth
					label={"新しいパスワード"}
					type="password"
					autoComplete="new-password"
					sx={{
						backgroundColor: "white",
					}}
					{...register("newPassword")}
					error={Boolean(errors.newPassword)}
				/>
				<Typography variant="caption" color="error" sx={{ display: "block" }}>
					{errors.newPassword?.message}
				</Typography>
				<TextField
					margin="normal"
					required
					fullWidth
					label={"新しいパスワードの確認"}
					type="password"
					sx={{
						backgroundColor: "white",
					}}
					{...register("newConfirmPassword")}
					error={Boolean(errors.newConfirmPassword)}
				/>
				<Typography variant="caption" color="error" sx={{ display: "block" }}>
					{errors.newConfirmPassword?.message}
				</Typography>
				<LoadingButton
					loading={isLoading}
					type="submit"
					fullWidth
					variant="contained"
					sx={{ mt: 3, mb: 2, fontWeight: "bold" }}
				>
					パスワードの再設定
				</LoadingButton>
			</Box>
		</Box>
	);
};
